class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

// One bounded retry for a missing leading zero. Liquid verifies a real, exact
// SKU before returning anything; title/body/prefix matches cannot become aliases.
window.BuildNexaSkuSearch = {
  alternateQuery(query) {
    return /^[1-9][0-9]{3,17}$/.test(query) ? `0${query}` : null;
  },

  async find(query, signal) {
    const alternate = this.alternateQuery(query);
    if (!alternate) return null;
    const params = new URLSearchParams({
      q: alternate,
      type: 'product',
      'options[prefix]': 'none',
      section_id: 'buildnexa-sku-fallback',
    });
    const response = await fetch(`${window.Shopify.routes.root}search?${params}`, { signal });
    if (!response.ok) throw new Error(`SKU search: ${response.status}`);
    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    const list = document.querySelector('[data-sku-results]');
    if (!list) return null;
    const seen = new Set();
    list.querySelectorAll('[data-sku-match]').forEach((item) => {
      if (item.dataset.sku !== alternate || seen.has(item.dataset.skuMatch)) item.remove();
      else seen.add(item.dataset.skuMatch);
    });
    return list.children.length ? list : null;
  },
};

class SkuSearchFallback extends HTMLElement {
  async connectedCallback() {
    const query = this.dataset.query;
    if (!window.BuildNexaSkuSearch.alternateQuery(query)) return;
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');
    try {
      const list = await window.BuildNexaSkuSearch.find(query, this.controller.signal);
      if (!this.isConnected || !list) return;
      this.querySelector('[role="status"]').textContent = `SKU matches for “${query}” with a leading zero included:`;
      this.append(list);
    } catch (error) {
      // Keep Shopify's original no-results message if the optional retry fails.
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

customElements.define('sku-search-fallback', SkuSearchFallback);
